const SECTION_ROUTES = Object.freeze([
  { pattern: /^\/calendar(?:\/|$)/i, section: "calendar" },
  { pattern: /^\/bookings(?:\/|$)/i, section: "bookings" },
  { pattern: /^\/customers(?:\/|$)/i, section: "clients" },
  { pattern: /^\/client-insights(?:\/|$)/i, section: "insights" },
  { pattern: /^\/services(?:\/|$)/i, section: "services" },
  { pattern: /^\/team(?:\/|$)/i, section: "team" },
  { pattern: /^\/pro\/settings(?:\/|$)/i, section: "settings" },
]);

const PROVIDER_ROUTES = Object.freeze([
  /^\/availability(?:\/|$)/i,
  /^\/onboarding(?:\/|$)/i,
  /^\/booking-page-builder(?:\/|$)/i,
  /^\/booking-page-visual-editor(?:\/|$)/i,
  /^\/profile-appearance(?:\/|$)/i,
  /^\/booking-questions(?:\/|$)/i,
  /^\/payments(?:\/|$)/i,
]);

// Routes any workspace member may open, whatever their sections allow. The
// Overview is the dashboard's landing page, so it has to be reachable by
// everyone who can reach the dashboard at all.
const WORKSPACE_OPEN_ROUTES = Object.freeze([
  /^\/pro\/overview(?:\/|$)/i,
  /^\/feedback(?:\/|$)/i,
]);

const pathnameOf = (value = "") => String(value).split("?")[0].split("#")[0] || "/";

export const canAccessWorkspaceRoute = ({
  path,
  capabilities,
  section = null,
  providerRequired = false,
  managerCapability = null,
  workspaceAllowed = false,
} = {}) => {
  if (!capabilities?.isWorkspaceMember) return false;
  if (providerRequired && !capabilities.canAccessProviderProfile) return false;
  if (managerCapability && !capabilities[managerCapability]) return false;
  // A list means any one of those sections opens the route.
  if (section) {
    return (Array.isArray(section) ? section : [section]).some((name) => capabilities.sections?.[name] === true);
  }
  if (workspaceAllowed) return true;

  const pathname = pathnameOf(path);
  if (WORKSPACE_OPEN_ROUTES.some((pattern) => pattern.test(pathname))) return true;

  const sectionRoute = SECTION_ROUTES.find(({ pattern }) => pattern.test(pathname));
  if (sectionRoute) return capabilities.sections?.[sectionRoute.section] === true;
  if (PROVIDER_ROUTES.some((pattern) => pattern.test(pathname))) {
    return capabilities.canAccessProviderProfile === true;
  }
  if (/^\/add-service(?:\/|$)/i.test(pathname)) {
    return capabilities.sections?.services === true && capabilities.canManageServices === true;
  }
  return false;
};

export const getActiveWorkspaceCapabilities = (user) =>
  user?.activeBusiness?.viewerCapabilities ||
  (Array.isArray(user?.businesses) && user.businesses.length === 1
    ? user.businesses[0]?.viewerCapabilities
    : null);

// Where a signed-in professional lands by default.
export const DEFAULT_DASHBOARD_PATH = "/pro/overview";

export const getWorkspaceLandingPath = (user, fallback = "/") =>
  user?.workspaceLandingPath ||
  getActiveWorkspaceCapabilities(user)?.workspaceLandingPath ||
  fallback;
